import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProfile, useUpdateProfile } from "@/features/profile";
import { profileAPI } from "@/features/profile/api";
import { ProfileUpdateRequest } from "@/features/profile/model/profile-types";
import { SignupFormData } from "@/features/signup/model/signup-types";
import { showAlert } from "@/shared/lib/alert";
import { writeAccessToken, writeRefreshToken } from "@/shared/lib/auth";

/**
 * 마이페이지 프로필 데이터 관련 hook
 */
export const useProfilePageData = () => {
  const { data: profileData, isLoading } = useProfile();

  const initialData = useMemo(() => {
    return profileData?.user
      ? {
          email: profileData.user.email || "",
          username: profileData.user.name || "",
          user_phone: profileData.user.user_phone || "",
          user_type: profileData.user.user_type || "individual",
          gender: undefined,
          address: "",
          manager_name: profileData.user.manager_name || "",
          manager_phone: profileData.user.manager_phone || "",
          isEmailVerified: true,
          agreeToTerms: true,
          agreeToPrivacy: true,
        }
      : undefined;
  }, [
    profileData?.user?.email,
    profileData?.user?.name,
    profileData?.user?.user_phone,
    profileData?.user?.user_type,
    profileData?.user?.manager_name,
    profileData?.user?.manager_phone,
  ]);

  return { profileData, isLoading, initialData };
};

/**
 * 마이페이지 프로필 수정 handler
 */
export const useProfilePageHandlers = (onEditSuccess?: () => void) => {
  const navigate = useNavigate();
  const updateProfileMutation = useUpdateProfile();

  const handleEditSubmit = async (data: SignupFormData) => {
    try {
      const updateData: ProfileUpdateRequest = {};

      // 입력된 값만 업데이트 객체에 추가
      if (data.email) {
        updateData.email = data.email;
      }
      if (data.username) {
        updateData.username = data.username;
      }
      if (data.gender) {
        updateData.gender = data.gender;
      }
      if (data.address) {
        updateData.address = data.address;
      }
      if (data.address_detail) {
        updateData.address_detail = data.address_detail;
      }
      if (data.password && data.password.length > 0) {
        updateData.password = data.password;
      }
      if (data.user_phone) {
        updateData.user_phone = data.user_phone;
      }
      // 담당자 정보 (유치원/프랜차이즈)
      if (data.manager_name) {
        updateData.manager_name = data.manager_name;
      }
      if (data.manager_phone) {
        updateData.manager_phone = data.manager_phone;
      }

      const result = await updateProfileMutation.mutateAsync(updateData);
      console.log("Profile update successful:", result);
      showAlert("회원정보가 수정되었습니다");
      onEditSuccess?.();
    } catch (error) {
      console.error("Profile update failed:", error);
      showAlert(`수정 실패: ${error}`);
    }
  };

  const handleWithdraw = async (password: string) => {
    try {
      const result = await profileAPI.deactivateAccount(password);

      if (!result.success) {
        showAlert(
          result.message || "탈퇴 실패: 알 수 없는 오류가 발생했습니다"
        );
        return;
      }

      showAlert("회원 탈퇴가 완료되었습니다");
      // 토큰 제거 및 로그아웃
      writeAccessToken("");
      writeRefreshToken("");
      navigate("/");
    } catch (error) {
      console.error("Withdrawal failed:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "탈퇴 실패: 알 수 없는 오류가 발생했습니다";
      showAlert(errorMessage);
    }
  };

  return {
    handleEditSubmit,
    handleWithdraw,
  };
};
